import { Divider, Grid, TextField, Typography } from "@mui/material";
import {
  isDistanceInRepExercise,
  isTimeExerciseInRep,
} from "@readyfit-common/models";
import { useState } from "react";
import { ApprovalButton } from "src/grading/approvalButton";
import { ExerciseChecklist } from "src/grading/exerciseChecklist";
import {
  GradeResult,
  RepCountGradeResult,
  RepTimeGradeResult,
} from "src/grading/models";
import { DisplayTimeFromSeconds, GetRestTime } from "src/grading/utils";
import { useAppSelector } from "src/store/hooks";
import {
  selectGradingStep,
  selectGradingTest,
} from "src/store/reducers/grading/gradingSlice";
import { TestStepDefinitionFormat } from "src/store/reducers/tests/testsApiSlice";
import {
  DecimalStepperInput,
  IntegerStepperInput,
  TimeStepperInput,
} from "./StepperInput";

interface RepsStepperProps {
  onGrade: (stepGrade: GradeResult) => void;

  currentGrade: GradeResult;
  stepMetadata: TestStepDefinitionFormat | undefined;
  prevEnd: number | null; // The end time of the previous step, for rest time displays
}

const RepsStepper = ({
  onGrade,
  currentGrade,
  stepMetadata,
  prevEnd,
}: RepsStepperProps) => {
  const test = useAppSelector(selectGradingTest);
  const step = useAppSelector(selectGradingStep);
  const [approved, setApproved] = useState(true);
  const exercise = test?.exercises[step];
  const isTimeExercise =
    stepMetadata?.exerciseType === "minTime" ||
    stepMetadata?.exerciseType === "maxTime" ||
    stepMetadata?.exerciseType === "cardio";
  const isTimeMilliseconds =
    isTimeExercise &&
    stepMetadata?.testing?.userEnteredResult?.type === "timeMilliseconds";

  if (!test || !stepMetadata) {
    return null;
  }

  return (
    <Grid container height={"80vh"} overflow={"scroll"}>
      <Grid item p={4} width={"100%"}>
        {stepMetadata.testing?.restTime !== undefined && (
          <>
            <Typography variant="subtitle1" textTransform={"uppercase"}>
              Rest Between Exercises
            </Typography>
            <Grid container justifyContent={"space-between"} mt={2.5}>
              <Typography color={"#666"} variant="caption">
                Rest Allowed
              </Typography>

              <Typography variant="caption">
                {" "}
                {DisplayTimeFromSeconds(
                  stepMetadata?.testing?.restTime?.maxSeconds || 0
                ) === "00:00"
                  ? "-"
                  : DisplayTimeFromSeconds(
                      stepMetadata?.testing?.restTime?.maxSeconds || 0
                    )}
              </Typography>
            </Grid>
            <Divider sx={{ marginTop: 1, marginBottom: 1 }} />

            <Grid container justifyContent={"space-between"}>
              <Typography color={"#666"} variant="caption">
                Rest Taken
              </Typography>

              <Typography variant="caption">
                {" "}
                {GetRestTime(prevEnd, exercise?.metadata?.startTime || 0)}
              </Typography>
            </Grid>

            <ApprovalButton
              onClick={(state) => {
                console.log("state", state);
                setApproved(state);
                const gradeResult = {
                  ...(currentGrade as RepCountGradeResult),
                  approved: state,
                  userResult: {
                    ...currentGrade.userResult,
                    fileKey: test?.exercises[step]?.fileKey as string,
                  },
                  exceededRestTime: !state,
                };

                onGrade(gradeResult);
              }}
              default={!currentGrade?.exceededRestTime}
            />
          </>
        )}

        {/* REPS COUNTER */}
        <Typography variant="subtitle1" textTransform={"uppercase"} mt={4}>
          {stepMetadata.testing?.userEnteredResult?.label ||
            (stepMetadata?.testing &&
              isTimeExerciseInRep(stepMetadata?.testing) &&
              "Time") ||
            (stepMetadata?.results &&
              isDistanceInRepExercise(stepMetadata?.results) &&
              "Distance (In Feet)") ||
            "Reps"}
        </Typography>

        {!isTimeExercise && (
          <IntegerStepperInput
            placeholder="0"
            value={(currentGrade as RepCountGradeResult).graderCount}
            onChange={(value) =>
              onGrade({
                ...currentGrade,
                graderCount: value,
                userResult: {
                  ...currentGrade.userResult,
                  fileKey: test?.exercises[step]?.fileKey as string,
                },
              } as RepCountGradeResult)
            }
          />
        )}
        {isTimeExercise && isTimeMilliseconds && (
          <DecimalStepperInput
            placeholder="0.0"
            precision={0.1}
            step={0.1}
            value={(currentGrade as RepCountGradeResult).graderCount}
            onChange={(value) =>
              onGrade({
                ...currentGrade,
                graderCount: value,
                userResult: {
                  ...currentGrade.userResult,
                  fileKey: test?.exercises[step]?.fileKey as string,
                },
              } as RepCountGradeResult)
            }
          />
        )}
        {isTimeExercise && !isTimeMilliseconds && (
          <TimeStepperInput
            placeholder="00:00"
            value={(currentGrade as RepTimeGradeResult).graderTime}
            onChange={(value) =>
              onGrade({
                ...currentGrade,
                graderTime: value,
                userResult: {
                  ...currentGrade.userResult,
                  fileKey: test?.exercises[step]?.fileKey as string,
                },
              } as RepTimeGradeResult)
            }
          />
        )}

        {/* USER REPS */}
        {exercise?.metadata?.userReps || exercise?.metadata?.reps ? (
          <>
            <Grid container mt={2}>
              <Typography variant="body1" fontWeight="bold" mr={2}>
                User Reported Score:
              </Typography>
            </Grid>
            <Grid container>
              <Typography variant="body1" fontWeight="bold">
                {exercise?.metadata?.userReps || exercise?.metadata?.reps}
              </Typography>
            </Grid>
          </>
        ) : null}

        {/* READYFIT AUTO SCORE */}
        {exercise?.metadata?.autoScoreValue && (
          <>
            <Grid container mt={2}>
              <Typography variant="body1" fontWeight="bold" mr={2}>
                ReadyFit Auto Score:
              </Typography>
            </Grid>
            <Grid container>
              <Typography variant="body1" fontWeight="bold">
                {exercise?.metadata?.autoScoreValue}
              </Typography>
            </Grid>
          </>
        )}
        {exercise?.metadata?.formCorrections &&
          (exercise?.metadata?.formCorrections as []).length > 0 && (
            <>
              <Grid container mt={1}>
                <Typography variant="body1" fontWeight="bold">
                  Auto Score Notes:
                </Typography>
              </Grid>
              <Grid container>
                <Typography variant="body1" fontWeight="bold">
                  {(exercise?.metadata?.formCorrections as []).join(",")}
                </Typography>
              </Grid>
            </>
          )}

        {/* NOTES */}
        <Typography variant="subtitle1" textTransform={"uppercase"} mt={4}>
          Notes
        </Typography>

        <Grid container>
          <TextField
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            placeholder="Enter notes here..."
            value={currentGrade?.userResult?.graderNotes ?? ""}
            onChange={(e) => {
              const updatedCurrentGrade = {
                ...currentGrade,
                userResult: {
                  ...currentGrade.userResult,
                  fileKey: test?.exercises[step]?.fileKey as string,
                  graderNotes: e.target.value,
                },
              };
              onGrade(updatedCurrentGrade);
            }}
          />
        </Grid>

        {/* DISQUALIFIERS */}
        <Typography
          variant="subtitle1"
          textTransform={"uppercase"}
          mt={4}
          mb={1}
        >
          Disqualifiers
        </Typography>
        {stepMetadata !== undefined && (
          <ExerciseChecklist
            stepMetadata={stepMetadata}
            exerciseName={stepMetadata.displayName!}
            onChange={(selections) => {
              const updatedCurrentGrade = {
                ...currentGrade,
                userResult: {
                  ...currentGrade.userResult,
                  fileKey: test?.exercises[step]?.fileKey as string,
                },
              };

              const currGrades = {
                ...updatedCurrentGrade,
                discrepancies: selections,
              };

              if (currGrades) {
                onGrade(currGrades);
              }
            }}
            selections={currentGrade?.discrepancies}
            key={stepMetadata.exerciseId}
          />
        )}
      </Grid>
    </Grid>
  );
};

export default RepsStepper;
